import React, { useEffect, useState } from "react";
import clsx from "clsx";
import styled from "styled-components/macro";
import { rgba } from "polished";
import { NavLink, withRouter, RouteComponentProps } from "react-router-dom";
import { darken } from "polished";
import { RouteType, RouteChildType } from "../types/routes";
import PerfectScrollbar from "react-perfect-scrollbar";
import "../vendor/perfect-scrollbar.css";
import MenuIcon from "@material-ui/icons/Menu";
import UserDropdown from "./UserDropdown";
import {
  Badge,
  Chip,
  Grid,
  Avatar,
  List as MuiList,
  ListItem,
  ListItemText,
  makeStyles,
  createStyles,
  Theme,
  Drawer,
  AppBar,
  Toolbar as TopBar,
  ListItemIcon,
  Box,
  Tooltip,
  Divider,
  Menu,
  IconButton,
  MenuItem
} from "@material-ui/core";
import { sidebarRoutes, bottomSidebarRoutes as bottomRoutes } from "../routes/index";
import { ReactComponent as Logo } from "../vendor/logo.svg";
import { useDispatch, useSelector } from "react-redux";
import { AppStateType } from "../redux/reducers";
import { fetchUser } from "../redux/actions/userActions";
import LanguagesDropdown from "../components/LanguagesDropdown";
import { filteredRoutesByProduct } from "../utils/functions";
import { IContract } from "../models/Contract";
import { getContractData } from "../selectors";
import { fetchContract } from "../redux/actions/contractsAction";
import { useTranslation } from "react-i18next";

const drawerWidth = 60;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
    },
    navWrapper: {
      backgroundColor: "#233044", 
      display: "flex", 
      position: "relative"
    },
    appBar: {
      height: "70px",
      backgroundColor: "#232F3E",
      zIndex: theme.zIndex.drawer + 1,
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      [theme.breakpoints.down("sm")]: {
        height: "60px"
      },
    },
    menuButton: {
      margin: 0
    },
    hide: {
      display: "none",
    },
    logoBox: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#1b2430",
      minHeight: "65px"
    },
    drawer: {
      display: "flex",
      height: "100vh",
      position: "fixed",
      zIndex: 999,
      overflowX: "hidden"
    },
    drawerOpen: {
      overflowX: "hidden",
      width: drawerWidth,
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    drawerClose: {
      overflowX: "hidden",
      width: "0px",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      })
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      padding: theme.spacing(0, 1),
      ...theme.mixins.toolbar,
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
    },
    hamburger: {
      width: `${drawerWidth}px`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    hamburgerIcon: {
      width: "30px",
      height: "30px",
      color: "#ffffff"
    },
    appBarHeadeItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    topBarWrapper : {
      width: "100vw", 
      height: "100%",
      paddingLeft: 0
    },
    topBar: {
      display: "flex",
      width: "100%",
      height: "100%",
      justifyContent: "space-between"
    },
    scrollBar: {
      flexGrow: 1,
      backgroundColor: "#1b2430",
      borderRight: "1px solid rgba(0, 0, 0, 0.12)",
      height: "initial !important",
      zIndex: 99999,
      marginTop: "60px",
      [theme.breakpoints.down("sm")]: {
        marginTop: "50px"
      },
    }
  })
);

const List = styled(MuiList)`
  background-color: #1b2430;
`;

const Items = styled.div`
  padding-top: ${(props) => props.theme.spacing(2.5)}px;
  padding-bottom: ${(props) => props.theme.spacing(2.5)}px;
`;

const BrandLogo = styled(Logo)`
  color: #ffffff;
  fill:  #ffffff;
  width: 128px;
  height: 33px;
  & path {
    fill: #ffffff
  }
`;

const Link = styled(ListItem)<{
  activeClassName: string;
  component: typeof NavLink;
  exact: boolean;
  to: string;
}>`
  padding-left: ${(props) => props.theme.spacing(17.5)}px;
  padding-top: ${(props) => props.theme.spacing(2)}px;
  padding-bottom: ${(props) => props.theme.spacing(2)}px;

  span {
    color: ${(props) => rgba(props.theme.sidebar.color, 0.7)};
  }

  &:hover span {
    color: ${(props) => rgba(props.theme.sidebar.color, 0.9)};
  }

  &:hover {
    background-color: ${(props) =>
      darken(0.015, props.theme.sidebar.background)};
  }

  &.${(props) => props.activeClassName} {
    background-color: ${(props) =>
      darken(0.03, props.theme.sidebar.background)};

    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`;

const LinkText = styled(ListItemText)`
  color: ${(props) => props.theme.sidebar.color};
  span {
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
  }
  margin-top: 0;
  margin-bottom: 0;
`;

const LinkBadge = styled(Chip)`
  font-size: 11px;
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 28px;
  top: 8px;
  background: ${(props) => props.theme.sidebar.badge.background};

  span.MuiChip-label,
  span.MuiChip-label:hover {
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)}px;
    padding-right: ${(props) => props.theme.spacing(2)}px;
  }
`;

const SidebarFooter = styled.div`
  background-color: ${(props) =>
    props.theme.sidebar.footer.background} !important;
  padding: ${(props) => 
    props.theme.spacing(2.75)}px
    ${(props) => props.theme.spacing(2)}px;
`;

const SidebarFooterBadge = styled(Badge)`
  margin-right: ${(props) => props.theme.spacing(1)}px;
  span {
    background-color: ${(props) =>
      props.theme.sidebar.footer.online.background};
    height: 12px;
    width: 12px;
    border-radius: 50%;
  }
`;

const ListItemCategory = styled(ListItem)`
    height: 59px;
    width: 59px;
    &:hover svg {
      color: #0E75BA;
    }
    &:hover {
      background-color: #1F2C3E;
    }
`;

type SidebarCategoryPropsType = {
  name: string;
  icon: JSX.Element;
  classes?: string;
  isOpen?: boolean;
  isCollapsable: boolean;
  badge?: string | number;
  isSelected: boolean,
  activeClassName?: string;
  button: true;
  onClick?: any;
  to?: string;
  component?: typeof NavLink;
  exact?: boolean;
};

const SidebarCategory: React.FC<SidebarCategoryPropsType> = ({
  name,
  icon,
  classes,
  isOpen,
  isCollapsable,
  badge,
  isSelected,
  ...rest
}) => {

  const style = {
    category: isSelected ? {
      backgroundColor: '#1F2C3E'
    } : {},
    icon: isSelected ? {
      color: '#0E75BA',
      minWidth: "auto"
    } : {
      color: "#84898F",
      minWidth: "auto"
    }
  }

  return (
    <Tooltip
      title={<p style={{fontSize: "13px", margin: "2px"}}>{name}</p>}
      placement="right"
      arrow
    >
      <ListItemCategory {...rest} style={style.category} >
        <ListItemIcon style={style.icon}>{icon}</ListItemIcon>
      </ListItemCategory>
    </Tooltip>
  );
};

type SidebarLinkPropsType = {
  name: string;
  to: string;
  badge?: string | number;
  icon?: JSX.Element;
};

const SidebarLink: React.FC<SidebarLinkPropsType> = ({ name, to, badge }) => {
  return (
    <Link
      button
      dense
      component={NavLink}
      exact
      to={to}
      activeClassName="active"
      style={{padding: "15px"}}
    >
      <LinkText>{name}</LinkText>
      {badge ? <LinkBadge label={badge} /> : ""}
    </Link>
  );
};

type SidebarPropsType = {
  classes?: string;
  staticContext: string;
  location: {
    pathname: string;
  };
  routes: Array<RouteType>;
  PaperProps: {
    style: {
      width: any;
    };
  };
  variant?: "permanent" | "persistent" | "temporary";
  drawerOpen?: boolean;
  handleDrawerToggle?: () => void;
};

type tplotOptions = {
  [key: number]: boolean;
};

const Sidebar: React.FC<RouteComponentProps & SidebarPropsType> = ({
  staticContext,
  location,
  drawerOpen,
  handleDrawerToggle
}) => {
  const classes = useStyles();
  const user: any = useSelector(
    (state: AppStateType) => state.authReducer.user
  );
  const { t } = useTranslation();

  const userProfile: any = useSelector(
    (state: AppStateType) => state.userReducer.user
  );
  const contractData = useSelector<IContract | any>(getContractData)
  const [routes, setRoutes] = useState<any>([sidebarRoutes[0]])
  const dispatch = useDispatch();
  useEffect(() => {
    if (user) {
      dispatch(fetchUser(user.user, user.token));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (userProfile) {
      dispatch(fetchContract(userProfile.company, user!.token))
    }
  }, [userProfile])

  useEffect(() => {
    if (contractData) {
      const filteredRoutes = filteredRoutesByProduct(contractData, userProfile, sidebarRoutes)
      setRoutes(filteredRoutes)
    }
  }, [contractData])

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const initOpenRoutes = (): tplotOptions => {
    /* Open collapse element that matches current url */
    const pathName = location.pathname;
    let _routes = {};
    routes.forEach((route: RouteType, index: number) => {
      const isActive = pathName.indexOf(route.path) === 0;
      const isOpen = route.open;
      const isHome = route.containsHome && pathName === "/";
      _routes = Object.assign({}, _routes, {
        [index]: isActive || isOpen || isHome,
      });
    });
    return _routes;
  };

  const initSelectedRouteId = () => {
    let currentLocationId = routes[0].id
    routes.concat(bottomRoutes).forEach((route: RouteType) => {
      if (location.pathname.includes(route.path)) {
        currentLocationId = route.id
      }
      // for children
      if (route.children && route.children.length > 0) {
        route.children.forEach((c) => {
          if (location.pathname.includes(c.path)) {
            currentLocationId = route.id
          }
        })
      }
    })
    return currentLocationId
  }

  const [openRoutes, setOpenRoutes] = useState(() => initOpenRoutes());
  const selectedRouteId = initSelectedRouteId()

  const toggle = (event: any, index: number) => {
    // Collapse all elements
    Object.keys(openRoutes).forEach((item) =>
      openRoutes[index] ||
      setOpenRoutes((openRoutes) => Object.assign({}, openRoutes, { [item]: false }))
    );
    // Toggle selected element
    setOpenRoutes((openRoutes) => Object.assign({}, openRoutes, { [index]: !openRoutes[index] }));
    const anc = event ? event.currentTarget : event;
    setAnchorEl(anc);
  };
  return (
    <div className={classes.navWrapper}>
      <AppBar
        position="fixed"
        className={clsx(classes.appBar)}
      >
        <TopBar className={classes.topBarWrapper}>
          <Grid className={classes.topBar}>
            <Grid className={classes.appBarHeadeItem}>
              <Box className={classes.hamburger}>
                <Tooltip title={String(t("menu"))}>
                  <IconButton
                    aria-label="open drawer"
                    onClick={handleDrawerToggle}
                    edge="start"
                    className={classes.menuButton}
                  >
                    <MenuIcon className={classes.hamburgerIcon} />
                  </IconButton>
                </Tooltip>
              </Box>
              <BrandLogo />
            </Grid>
            <Grid className={classes.appBarHeadeItem}>
              <LanguagesDropdown />
              <UserDropdown />
            </Grid>
          </Grid>
        </TopBar>
      </AppBar>
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: drawerOpen,
          [classes.drawerClose]: !drawerOpen,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: drawerOpen,
            [classes.drawerClose]: !drawerOpen,
          }),
        }}
      >
        <PerfectScrollbar className={classes.scrollBar}>
          <List disablePadding>
            <Items>
              {routes.map((category: RouteType, index: number) => (
                <React.Fragment key={index}>
                  {(category.children && category.icon) ? (
                    <React.Fragment>
                      <SidebarCategory
                        isOpen={!openRoutes[index]}
                        isCollapsable={false}
                        name={category.id}
                        icon={category.icon}
                        button={true}
                        isSelected={selectedRouteId === category.id}
                        onClick={(e: React.MouseEventHandler<HTMLElement>) => toggle(e, index)}
                      />
                      <Menu
                        anchorEl={anchorEl}
                        id={category.id}
                        open={(openRoutes[index] === undefined) ? false : openRoutes[index]}
                        onClose={handleClose}
                        onClick={() => {
                          toggle(null, index);
                          handleClose();
                        }}
                        PaperProps={{
                          elevation: 1
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        style={{left: "46px"}}
                        MenuListProps={{
                          'aria-labelledby': 'basic-button',
                        }}
                      >
                        {category.children.map(
                          (route: RouteChildType, index: number) => (
                            <MenuItem
                              key={index}
                              style={{padding: "0", minWidth: "170px"}}
                            >
                              <SidebarLink
                                key={index}
                                name={route.name}
                                to={route.path}
                                icon={route.icon}
                                badge={route.badge}
                              />
                            </MenuItem>
                          )
                        )}
                      </Menu>
                    </React.Fragment>
                  ) : category.icon ? (
                    <SidebarCategory
                      isOpen={!openRoutes[index]}
                      isCollapsable={false}
                      name={category.id}
                      to={category.path}
                      activeClassName="active"
                      component={NavLink}
                      icon={category.icon}
                      isSelected={selectedRouteId === category.id}
                      exact
                      button
                      badge={category.badge}
                    />
                  ) : null}
                  {category?.dividerUnder ? 
                    <Divider 
                      style={{backgroundColor: "#eeeeee4d"}} 
                      variant="middle" 
                    /> : null}
                </React.Fragment>
              ))}
            </Items>
          </List>
        </PerfectScrollbar>
        <List disablePadding>
          <Items>
            {bottomRoutes.map((category: RouteType, index: number) => (
              <React.Fragment key={index}>
                {category.icon ? (
                  <SidebarCategory
                    isCollapsable={false}
                    name={category.id}
                    to={category.path}
                    activeClassName="active"
                    component={NavLink}
                    icon={category.icon}
                    isSelected={selectedRouteId === category.id}
                    exact
                    button
                    badge={category.badge}
                  />
                ) : null}
              </React.Fragment>
            ))}
          </Items>
        </List>
        <SidebarFooter>
          <Grid container spacing={2}>
            <Grid item>
              <SidebarFooterBadge
                overlap="circle"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                variant="dot"
              >
                <Avatar 
                  alt={`${userProfile?.first_name} ${userProfile?.last_name}`}
                  src={userProfile?.picture} 
                />
              </SidebarFooterBadge>
            </Grid>
          </Grid>
        </SidebarFooter>
      </Drawer>
    </div>
  );
};

export default withRouter(Sidebar);
